import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/header';
import { useLanguage } from '../../../helpers/language-context';
import { supabase } from '../../../supabaseClient';

const NewsRelease = () => {
  const { translations, language } = useLanguage();
  const [news, setNews] = useState([]);
  const [filteredNews, setFilteredNews] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters state
  const [filterYear, setFilterYear] = useState('All Time');
  const [sortOrder, setSortOrder] = useState('Newest');

  // Dropdown visibility state
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // Refs for click outside handling
  const yearDropdownRef = useRef(null);
  const sortDropdownRef = useRef(null);

  useEffect(() => {
    const fetchNews = async () => {
      const { data, error } = await supabase
        .from('newsfeed')
        .select('*')
        .order('date', { ascending: false });

      if (!error) {
        setNews(data);
        setFilteredNews(data);
      } else {
        console.error('Error fetching news:', error);
      }
      setLoading(false);
    };

    fetchNews();
  }, []);

  // Handle click outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (yearDropdownRef.current && !yearDropdownRef.current.contains(event.target)) {
        setShowYearDropdown(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Apply filters and sorting whenever state changes
  useEffect(() => {
    let result = [...news];

    // Apply Year Filter
    if (filterYear !== 'All Time') {
      result = result.filter(item => {
        if (!item.date) return false;
        const date = new Date(item.date);
        if (isNaN(date.getTime())) return false;
        return date.getFullYear().toString() === filterYear;
      });
    }

    // Apply Sorting
    result.sort((a, b) => {
      const dateA = a.date ? new Date(a.date) : new Date(0);
      const dateB = b.date ? new Date(b.date) : new Date(0);

      if (sortOrder === 'Newest') {
        return dateB - dateA;
      } else {
        return dateA - dateB;
      }
    });

    setFilteredNews(result);
  }, [news, filterYear, sortOrder]);

  const getLocalized = (item, field) => {
    return language === 'ms'
      ? (item[`${field}_ms`] || item[`${field}_en`])
      : (item[`${field}_en`] || item[`${field}_ms`]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString(language === 'ms' ? 'ms-MY' : 'en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Get unique years for filter
  const years = ['All Time', ...Array.from(new Set(
    news
      .map(s => s.date ? new Date(s.date).getFullYear() : null)
      .filter(y => y !== null && !isNaN(y))
  )).sort((a, b) => b - a).map(String)];

  const handleReset = () => {
    setFilterYear('All Time');
    setSortOrder('Newest');
  };

  return (
    <div className="ourbrand-container">
      <Header title={translations.media.newsRelease || 'News Release'} bgImg="/assets/images/header-bg/bg_news_new.jpg" />

      <section className="bg-white py-16 px-4 sm:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-12">

            {/* Year Filter Dropdown */}
            <div className="relative" ref={yearDropdownRef}>
              <button
                onClick={() => setShowYearDropdown(!showYearDropdown)}
                className="flex items-center justify-between gap-2 px-6 py-2.5 bg-white border border-[#28357B] rounded-full text-[#28357B] min-w-[140px] font-medium hover:bg-gray-50 transition-colors"
              >
                <span>{filterYear}</span>
                <svg className={`w-4 h-4 transition-transform duration-200 ${showYearDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showYearDropdown && (
                <div className="absolute top-full left-0 mt-2 w-full min-w-[140px] bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1">
                  {years.map(year => (
                    <button
                      key={year}
                      onClick={() => {
                        setFilterYear(year);
                        setShowYearDropdown(false);
                      }}
                      className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${filterYear === year ? 'text-[#28357B] font-bold bg-blue-50' : 'text-gray-700'}`}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Sort Order Dropdown */}
            <div className="relative" ref={sortDropdownRef}>
              <button
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                className="flex items-center justify-between gap-2 px-6 py-2.5 bg-white border border-[#28357B] rounded-full text-[#28357B] min-w-[140px] font-medium hover:bg-gray-50 transition-colors"
              >
                <span>{sortOrder}</span>
                <svg className={`w-4 h-4 transition-transform duration-200 ${showSortDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showSortDropdown && (
                <div className="absolute top-full left-0 mt-2 w-full min-w-[140px] bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1">
                  {['Newest', 'Oldest'].map(order => (
                    <button
                      key={order}
                      onClick={() => {
                        setSortOrder(order);
                        setShowSortDropdown(false);
                      }}
                      className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${sortOrder === order ? 'text-[#28357B] font-bold bg-blue-50' : 'text-gray-700'}`}
                    >
                      {order}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Reset Button */}
            <button
              onClick={handleReset}
              className="px-8 py-2.5 bg-[#28357B] text-white rounded-full font-medium hover:bg-[#1e2a69] transition-colors"
            >
              {translations.button.resetFilter}
            </button>
          </div>

          {/* Grid */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#28357B]"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredNews.map((item) => (
                <Link to={`/media/news-release/details/${item.id}`} key={item.id} className="group cursor-pointer flex flex-col h-full">
                  <div className="relative overflow-hidden rounded-2xl mb-6 aspect-[4/3] border-0 group-hover:border-[3px] border-white group-hover:border-[#FDA729] transition-colors scale-102 group-hover:scale-105 transition-transform duration-500">
                    <img
                      src={item.cover_image || item.image}
                      alt={getLocalized(item, 'title')}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="flex flex-col flex-grow">
                    <span className="text-gray-400 text-sm mb-3">
                      {formatDate(item.date)}
                    </span>

                    <h3 className="text-[#28357B] text-xl font-bold mb-3 leading-tight line-clamp-2">
                      {getLocalized(item, 'title')}
                    </h3>

                    <p className="text-gray-600 text-base leading-relaxed line-clamp-3">
                      {getLocalized(item, 'description1')}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {!loading && filteredNews.length === 0 && (
            <div className="text-center py-20 text-gray-500">
              No news found matching your criteria.
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default NewsRelease;
